<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { useNewsletter, isInsufficientCreditsError } from '@/composables/useNewsletter'
import { useRecipientCount } from '@/composables/useRecipientCount'
import { useCreditBalance } from '@/composables/useCreditBalance'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import MarkdownEditor from '@/components/common/MarkdownEditor.vue'
import AudienceBuilderOverlay from '@/components/organizer/newsletter/AudienceBuilderOverlay.vue'
import SendConfirmationModal from '@/components/organizer/newsletter/SendConfirmationModal.vue'
import type { NewsletterAudience, SendNewsletterSuccess } from '@/types/newsletter'
import { NewsletterStatus, NewsletterType } from '@/types/newsletter'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { user, currentOrganisation } = useAuth()

const orgSlug      = computed<string>(() => route.params['orgSlug'] as string)
const newsletterId = computed<number>(() => Number(route.params['newsletterId']))
const orgId        = computed<number | null>(() => {
  const found = user.value?.organisations.find((o) => o.slug === orgSlug.value)
  return found?.id ?? null
})

const {
  newsletter,
  loading,
  saving,
  error,
  formErrors,
  saved,
  load,
  save,
  sendTest,
  send,
  cancel,
} = useNewsletter(orgId.value ?? 0)

const { count, loading: countLoading, refreshNow: refreshCount } = useRecipientCount(orgId.value ?? 0)
const { balance, refresh: refreshBalance } = useCreditBalance(orgId.value ?? 0)

// Form fields (local, synced from newsletter)
const title   = ref('')
const type    = ref<string>(NewsletterType.Newsletter)
const subject = ref('')
const body    = ref('')

// UI state
const showAudienceBuilder = ref(false)
const showSendModal       = ref(false)
const testSendMessage     = ref('')
const testSendError       = ref('')
const successMessage      = ref('')
const sendError           = ref('')
const cancelError         = ref('')

// Autosave debounce
let autosaveTimer: ReturnType<typeof setTimeout> | null = null
const AUTOSAVE_DELAY = 2000

// Whether the newsletter is editable
const isDraft = computed(() => newsletter.value?.status === NewsletterStatus.Draft)

// Audience from newsletter
const audience = computed<NewsletterAudience | null>(() => newsletter.value?.audience ?? null)

// Recipient count derived from API
const recipientTotal = computed<number>(() => count.value?.total ?? newsletter.value?.recipient_count ?? 0)
const creditCost     = computed<number>(() => recipientTotal.value)
const balanceAmount  = computed<number>(() => balance.value?.balance ?? 0)

function syncFormFromNewsletter(): void {
  if (!newsletter.value) return
  title.value   = newsletter.value.title
  type.value    = newsletter.value.type
  subject.value = newsletter.value.subject ?? ''
  body.value    = newsletter.value.body ?? ''
}

function scheduleAutosave(): void {
  if (!isDraft.value) return
  if (autosaveTimer) clearTimeout(autosaveTimer)
  autosaveTimer = setTimeout(async () => {
    if (!orgId.value || !newsletter.value) return
    await save(newsletter.value.id, {
      title: title.value,
      type: type.value as typeof NewsletterType[keyof typeof NewsletterType],
      subject: subject.value,
      body: body.value,
    })
  }, AUTOSAVE_DELAY)
}

// Sync field watchers to autosave
watch([title, type, subject, body], () => scheduleAutosave())

async function handleSaveNow(): Promise<void> {
  if (!orgId.value || !newsletter.value) return
  await save(newsletter.value.id, {
    title: title.value,
    type: type.value as typeof NewsletterType[keyof typeof NewsletterType],
    subject: subject.value,
    body: body.value,
  })
}

async function handleTestSend(): Promise<void> {
  if (!newsletter.value || !orgId.value) return
  testSendMessage.value = ''
  testSendError.value = ''
  const result = await sendTest(newsletter.value.id)
  if (result.success) {
    testSendMessage.value = result.message || t('newsletters.composer.test_send_default_success')
  } else {
    testSendError.value = result.message
  }
}

async function handleCancelSchedule(): Promise<void> {
  if (!newsletter.value || !orgId.value) return
  cancelError.value = ''
  const ok = await cancel(newsletter.value.id)
  if (!ok) {
    cancelError.value = t('newsletters.composer.cancel_error')
  }
}

function openSendModal(): void {
  // Save first before opening the modal
  void handleSaveNow().then(() => {
    showSendModal.value = true
  })
}

async function onSendConfirmed(result: SendNewsletterSuccess): Promise<void> {
  showSendModal.value = false
  successMessage.value = result.message || t('newsletters.status.sent')
  // Reload newsletter to reflect new status
  if (newsletter.value && orgId.value) {
    await load(newsletter.value.id)
  }
  await refreshBalance()
}

async function onAudienceSaved(): Promise<void> {
  showAudienceBuilder.value = false
  if (!newsletter.value || !orgId.value) return
  const id = newsletter.value.id
  await load(id)
  void refreshCount(id)
}

onMounted(async () => {
  if (!orgId.value) return

  await Promise.all([
    load(newsletterId.value),
    refreshBalance(),
  ])

  syncFormFromNewsletter()

  if (newsletter.value) {
    void refreshCount(newsletter.value.id)
  }
})

watch(newsletter, () => {
  // Don't overwrite the form if the user is actively editing
  if (!saving.value) {
    syncFormFromNewsletter()
  }
})

// TypeScript: type options for the dropdown
const TYPE_OPTIONS = [
  { value: NewsletterType.Briefing,   label: t('newsletters.type.briefing') },
  { value: NewsletterType.Debriefing, label: t('newsletters.type.debriefing') },
  { value: NewsletterType.Newsletter, label: t('newsletters.type.newsletter') },
  { value: NewsletterType.RaceUpdate, label: t('newsletters.type.race_update') },
]

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    draft:     t('newsletters.status.draft'),
    scheduled: t('newsletters.status.scheduled'),
    sending:   t('newsletters.status.sending'),
    sent:      t('newsletters.status.sent'),
    failed:    t('newsletters.status.failed'),
  }
  return map[status] ?? status
}

function formatDateTime(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// Redirect to list if load fails
watch(error, (e) => {
  if (e) {
    const slug = currentOrganisation.value?.slug
    if (slug) {
      void router.push(`/organizer/newsletters`)
    }
  }
})
</script>

<template>
  <OrganizerLayout>
    <!-- Loading state -->
    <div v-if="loading && !newsletter" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div class="flex items-center gap-3 text-slate-400">
        <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {{ t('newsletters.composer.loading') }}
      </div>
    </div>

    <div v-else-if="newsletter" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <!-- Back link -->
      <div class="mb-4">
        <RouterLink
          to="/organizer/newsletters"
          class="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          {{ t('newsletters.composer.back_link') }}
        </RouterLink>
      </div>

      <!-- Success message -->
      <div
        v-if="successMessage"
        class="mb-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
      >
        {{ successMessage }}
      </div>

      <!-- Non-editable notice -->
      <div
        v-if="!isDraft"
        class="mb-4 rounded-xl bg-slate-700/50 border border-slate-600 px-5 py-3 flex items-center justify-between gap-4"
      >
        <p class="text-sm text-slate-300">
          {{ t('newsletters.composer.non_editable_notice', { status: statusLabel(newsletter.status) }) }}
          <template v-if="newsletter.scheduled_at">{{ t('newsletters.composer.non_editable_scheduled', { datetime: formatDateTime(newsletter.scheduled_at) }) }}</template>
          {{ t('newsletters.composer.non_editable_suffix') }}
        </p>
        <button
          v-if="newsletter.status === 'scheduled'"
          type="button"
          class="shrink-0 text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors"
          @click="handleCancelSchedule"
        >
          {{ t('newsletters.composer.cancel_schedule_button') }}
        </button>
      </div>

      <div v-if="cancelError" class="mb-4 text-sm text-red-400">{{ cancelError }}</div>

      <!-- Two-column composer layout: stacked below lg, side-by-side at lg and up.
           items-stretch below lg so the editor column takes the full row width
           (items-start would size it to content width on the cross axis while
           stacked); lg:items-start restores the previous side-by-side behaviour
           so the aside stays sticky and does not stretch to the editor's height. -->
      <div class="flex flex-col lg:flex-row gap-6 items-stretch lg:items-start">
        <!-- Left: editor -->
        <div class="flex-1 min-w-0 space-y-4">
          <!-- Title -->
          <div>
            <label for="nl-title" class="block text-xs font-medium text-slate-400 mb-1">
              {{ t('newsletters.composer.title_label') }} <span class="text-slate-600">{{ t('newsletters.composer.title_note') }}</span>
            </label>
            <input
              id="nl-title"
              v-model="title"
              type="text"
              maxlength="255"
              :disabled="!isDraft"
              class="w-full bg-slate-800 border rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed"
              :class="formErrors['title'] ? 'border-red-500' : 'border-slate-600'"
              :placeholder="t('newsletters.composer.title_placeholder')"
            />
            <p v-if="formErrors['title']" class="mt-1 text-xs text-red-400">{{ formErrors['title'][0] }}</p>
          </div>

          <!-- Type -->
          <div>
            <label for="nl-type" class="block text-xs font-medium text-slate-400 mb-1">{{ t('newsletters.composer.type_label') }}</label>
            <select
              id="nl-type"
              v-model="type"
              :disabled="!isDraft"
              class="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option
                v-for="opt in TYPE_OPTIONS"
                :key="opt.value"
                :value="opt.value"
              >{{ opt.label }}</option>
            </select>
          </div>

          <!-- Subject -->
          <div>
            <label for="nl-subject" class="block text-xs font-medium text-slate-400 mb-1">
              {{ t('newsletters.composer.subject_label') }} <span class="text-red-400">*</span>
            </label>
            <input
              id="nl-subject"
              v-model="subject"
              type="text"
              maxlength="255"
              :disabled="!isDraft"
              class="w-full bg-slate-800 border rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed"
              :class="formErrors['subject'] ? 'border-red-500' : 'border-slate-600'"
              placeholder="See you Saturday: start times &amp; parking"
            />
            <p v-if="formErrors['subject']" class="mt-1 text-xs text-red-400">{{ formErrors['subject'][0] }}</p>
          </div>

          <!-- Body (markdown editor) -->
          <div>
            <label class="block text-xs font-medium text-slate-400 mb-1">
              {{ t('newsletters.composer.body_label') }} <span class="text-red-400">*</span>
            </label>
            <p v-if="formErrors['body']" class="mb-1 text-xs text-red-400">{{ formErrors['body'][0] }}</p>
            <MarkdownEditor
              v-model="body"
              :placeholder="t('newsletters.composer.body_placeholder')"
              :max-length="50000"
              :disabled="!isDraft"
            />
          </div>

          <!-- Autosave indicator -->
          <div class="flex items-center gap-2 text-xs text-slate-500">
            <span v-if="saving">{{ t('newsletters.composer.saving') }}</span>
            <span v-else-if="saved" class="text-emerald-400">{{ t('newsletters.composer.saved') }}</span>
          </div>
        </div>

        <!-- Right: sticky summary rail (full width and not sticky while stacked below lg) -->
        <aside class="w-full lg:w-72 lg:shrink-0 lg:sticky lg:top-20 space-y-4">
          <!-- Recipients section -->
          <div class="rounded-xl bg-slate-800/50 border border-slate-700 p-4 space-y-3">
            <h3 class="text-xs font-semibold text-slate-400 uppercase tracking-wider">{{ t('newsletters.composer.recipients_heading') }}</h3>

            <div v-if="audience" class="space-y-1">
              <div class="leading-snug">
                <p v-if="audience.event_name" class="text-xs font-medium text-white pb-1">{{ audience.event_name }}</p>
                <p v-if="audience.edition_name" class="text-xs font-medium text-white pb-1">{{ audience.edition_name }}</p>
                <p v-if="audience.race_name" class="text-xs font-medium text-white">{{ audience.race_name }}</p>
              </div>
              <p class="text-xs text-slate-400">
                {{ audience.registration_statuses.join(', ') }}
              </p>
              <p class="text-lg font-bold text-white">
                <span v-if="countLoading" class="text-slate-400 text-sm">{{ t('newsletters.composer.calculating_recipients') }}</span>
                <template v-else>{{ recipientTotal.toLocaleString() }}</template>
              </p>
              <p class="text-xs text-slate-500">{{ t('newsletters.composer.recipients_label') }}</p>
            </div>
            <p v-else class="text-sm text-slate-500 italic">{{ t('newsletters.composer.audience_not_set') }}</p>

            <button
              v-if="isDraft"
              type="button"
              class="w-full text-sm text-emerald-400 hover:text-emerald-300 transition-colors text-left"
              @click="showAudienceBuilder = true"
            >
              {{ audience ? t('newsletters.composer.edit_recipients') : t('newsletters.composer.build_recipients') }}
            </button>
          </div>

          <!-- Cost section -->
          <div class="rounded-xl bg-slate-800/50 border border-slate-700 p-4 space-y-2">
            <h3 class="text-xs font-semibold text-slate-400 uppercase tracking-wider">{{ t('newsletters.composer.cost_heading') }}</h3>
            <p class="text-lg font-bold text-white">
              {{ creditCost.toLocaleString() }} <span class="text-sm font-normal text-slate-400">{{ t('newsletters.composer.cost_credits') }}</span>
            </p>
            <div class="text-xs text-slate-500 space-y-1">
              <div class="flex justify-between">
                <span>{{ t('newsletters.composer.balance_now') }}</span>
                <span class="text-slate-300">{{ balanceAmount.toLocaleString() }}</span>
              </div>
              <div class="flex justify-between">
                <span>{{ t('newsletters.composer.balance_after_send') }}</span>
                <span :class="balanceAmount - creditCost >= 0 ? 'text-emerald-400' : 'text-red-400'">
                  {{ Math.max(0, balanceAmount - creditCost).toLocaleString() }}
                </span>
              </div>
            </div>
          </div>

          <!-- Test send -->
          <div v-if="isDraft" class="rounded-xl bg-slate-800/50 border border-slate-700 p-4 space-y-2">
            <h3 class="text-xs font-semibold text-slate-400 uppercase tracking-wider">{{ t('newsletters.composer.test_send_heading') }}</h3>
            <p class="text-xs text-slate-500">
              {{ t('newsletters.composer.test_sends_remaining', { count: newsletter.test_sends_remaining }) }}
            </p>
            <button
              type="button"
              :disabled="newsletter.test_sends_remaining === 0"
              class="w-full text-sm border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white rounded-lg px-3 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              @click="handleTestSend"
            >
              {{ t('newsletters.composer.test_send_button') }}
            </button>
            <p v-if="testSendMessage" class="text-xs text-emerald-400">{{ testSendMessage }}</p>
            <p v-if="testSendError" class="text-xs text-red-400">{{ testSendError }}</p>
          </div>

          <!-- Actions -->
          <div v-if="isDraft" class="space-y-2">
            <button
              type="button"
              :disabled="saving"
              class="w-full border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors disabled:opacity-50"
              @click="handleSaveNow"
            >
              {{ saving ? t('newsletters.composer.saving') : t('newsletters.composer.save_draft_button') }}
            </button>
            <button
              type="button"
              :disabled="!audience || recipientTotal === 0"
              class="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors"
              @click="openSendModal"
            >
              {{ t('newsletters.composer.continue_to_send_button') }}
            </button>
            <p v-if="!audience" class="text-xs text-slate-500 text-center">{{ t('newsletters.composer.build_recipients_first') }}</p>
            <p v-else-if="recipientTotal === 0" class="text-xs text-slate-500 text-center">{{ t('newsletters.composer.no_recipients_match') }}</p>
          </div>
        </aside>
      </div>
    </div>

    <!-- Audience builder overlay -->
    <AudienceBuilderOverlay
      v-if="showAudienceBuilder && newsletter && orgId"
      :newsletter-id="newsletter.id"
      :organisation-id="orgId"
      :model-value="audience"
      @close="showAudienceBuilder = false"
      @saved="onAudienceSaved"
    />

    <!-- Send confirmation modal -->
    <SendConfirmationModal
      v-if="showSendModal && newsletter && orgId"
      :newsletter-id="newsletter.id"
      :organisation-id="orgId"
      :recipient-count="recipientTotal"
      :credit-balance="balanceAmount"
      :org-slug="orgSlug"
      @confirmed="onSendConfirmed"
      @cancel="showSendModal = false"
    />
  </OrganizerLayout>
</template>
